import React, { useEffect, useState } from 'react'
import { CollectionThemeProps, InviteType, QuizInviteQuestionType, SubtitleType } from '../../../../../lib/types'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { intervalToDuration } from 'date-fns'
import Box from '@mui/material/Box'
import ButtonGroup from '@mui/material/ButtonGroup'
import CircularProgress from '@mui/material/CircularProgress'
import LoadingButton from '@mui/lab/LoadingButton'
import QuestionnaireInputFileArea from '../../../../../components/QuestionnaireInputFileArea'
import api from '../../../../../lib/api'
import Link from '@mui/material/Link'
import Checkbox from '@mui/material/Checkbox'
import Button from '@mui/material/Button'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

export type UploadState = 'idle' | 'uploading' | 'retrying' | 'success' | 'error'

const Step2: React.FC<{
  invite: InviteType
  title: string
  question: string
  questionObj: QuizInviteQuestionType
  handleNext: (file: File | null) => void
  progress: number
  uploadState: UploadState
  uploadAttempt: number
  maxAttempts: number
  uploadError: string
  restoredFile: File | null
  onDiscardRestored: () => void
  keepAudioOnly: boolean
  setKeepAudioOnly: (audioOnly: boolean) => void
  collectionTheme?: CollectionThemeProps
}> = ({
  progress,
  title,
  question,
  questionObj,
  invite,
  handleNext,
  uploadState,
  uploadAttempt,
  maxAttempts,
  uploadError,
  restoredFile,
  onDiscardRestored,
  collectionTheme,
  keepAudioOnly,
  setKeepAudioOnly
}) => {
  const [file, setFile] = useState<File | null>(null)

  // Restore an unsent recording recovered from the local backup (e.g. after a page reload).
  useEffect(() => {
    if (restoredFile) {
      setFile(restoredFile)
    }
  }, [restoredFile])

  const isUploading = uploadState === 'uploading' || uploadState === 'retrying'
  const duration = intervalToDuration({ start: 0, end: invite.requestedMediaLength * 1000 })

  // eslint-disable-next-line prefer-const
  const [subtitles, setSubtitles] = useState<SubtitleType[]>([])
  useEffect(() => {
    const getSubtitles = async () => {
      if (invite?.storyId) {
        // const response = await api.getSubtitleByStoryId(invite.storyId)
        // setSubtitles(response.content)
      }
    }
    // getSubtitles()
  }, [])

  const handleKeepAudioOnly = () => {
    console.log('In keepAudioOnly: ' + keepAudioOnly)
    keepAudioOnly = !keepAudioOnly
    setKeepAudioOnly(keepAudioOnly)
    console.log('Out keepAudioOnly: ' + keepAudioOnly)
  }

  const [urlBase, setUrlBase] = useState<string>('')
  useEffect(() => {
    const currentUrl = window.location.hostname
    setUrlBase(currentUrl)
    console.log(`The domain is: ${currentUrl}`)
  }, [])

  const isNotIOC = urlBase !== 'ioc.rakonto.io'

  const releaseForm =
    'https://objectstorage.eu-zurich-1.oraclecloud.com/n/idxwux4l0c8w/b/public/o/ioc_releaseform_2023-04-12.pdf'

  const terms = isNotIOC
    ? 'https://cdn.wfp.org/legal/terms/'
    : 'https://objectstorage.eu-zurich-1.oraclecloud.com/n/idxwux4l0c8w/b/public/o/ioc_terms_of_service_2023-04-12.pdf'

  const policy = isNotIOC ? 'https://www.wfp.org/privacy-policy' : 'https://olympics.com/en/privacy-policy'

  const entityText = isNotIOC ? ' World Food Program Terms' : ' International Olympic Committee Terms'

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        textAlign="center"
        sx={{
          color: collectionTheme?.questionnaireBodyTextColor,
          fontFamily: collectionTheme?.questionnaireTextFontFamily
        }}
      >
        <Box mb={1} mt={3} textAlign="center">
          <Typography
            variant="subtitle1"
            textAlign="center"
            fontSize={'0.9em'}
            fontFamily={collectionTheme?.questionnaireTextFontFamily}
          >
            {title}
          </Typography>
          <Typography
            textAlign={'center'}
            variant={'h4'}
            marginY={2}
            fontSize={'1.0em'}
            fontFamily={collectionTheme?.questionnaireTextFontFamily}
          >
            {questionObj.question}
          </Typography>
        </Box>
      </Grid>
      {uploadState === 'idle' ? (
        <>
          {isNotIOC && (
            <>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  verticalAlign: 'middle',
                  width: '100%',
                  margin: '-15px'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={keepAudioOnly}
                    onChange={handleKeepAudioOnly}
                    sx={{
                      color: collectionTheme?.questionnaireBodyTextColor,
                      '&.Mui-checked': { color: collectionTheme?.questionnaireLinksColor },
                      verticalAlign: 'middle'
                    }}
                  />
                  <Typography
                    variant="body2"
                    mb={2}
                    fontSize="1em"
                    sx={{
                      ml: 1,
                      marginTop: '1em',
                      verticalAlign: 'middle',
                      color: collectionTheme?.questionnaireBodyTextColor,
                      fontFamily: collectionTheme?.questionnaireTextFontFamily
                    }}
                  >
                    Save only the audio from this recording, discarding any images it contains.
                  </Typography>
                </Box>
              </Grid>
              <br></br>
            </>
          )}
          <Grid
            item
            xs={12}
            mt={4}
            mb={9}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              margin: '10px'
            }}
          >
            <QuestionnaireInputFileArea
              file={file}
              callback={newFile => {
                setFile(newFile)
                if (!newFile && restoredFile) {
                  onDiscardRestored()
                }
              }}
              hardStop={invite.hardStop}
              countdown={invite.requestedMediaLength}
              disableChangeMediaType={!!invite.requestedMediaType}
              startType={invite.requestedMediaType || null}
              media={invite.video}
              subtitles={subtitles}
              collectionTheme={collectionTheme}
            />
          </Grid>
        </>
      ) : isUploading ? (
        <Grid
          item
          xs={12}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 10, flexFlow: 'column' }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress size={100} variant="determinate" value={progress || 0} />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="h6"
                component="div"
                color="text.secondary"
                fontFamily={collectionTheme?.questionnaireTextFontFamily}
              >
                {`${Math.round(progress || 0)}%`}
              </Typography>
            </Box>
          </Box>
          {uploadState === 'retrying' && (
            <Typography
              mt={2}
              variant="h6"
              component="div"
              sx={{
                color: 'warning.main',
                fontFamily: collectionTheme?.questionnaireTextFontFamily
              }}
            >
              {`Connection issue - retrying (attempt ${uploadAttempt} of ${maxAttempts})...`}
            </Typography>
          )}
          <Typography
            mt={2}
            variant="h6"
            component="div"
            color="text.secondary"
            fontFamily={collectionTheme?.questionnaireTextFontFamily}
            sx={{
              color: collectionTheme?.questionnaireBodyTextColor,
              fontFamily: collectionTheme?.questionnaireTextFontFamily
            }}
          >
            Uploading your answer - this may take a few minutes. Please do not refresh or close your browser.
          </Typography>
        </Grid>
      ) : uploadState === 'success' ? (
        <Grid
          item
          xs={12}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 10, flexFlow: 'column' }}
        >
          <CheckCircleIcon sx={{ fontSize: 100, color: 'success.main' }} />
          <Typography
            mt={2}
            variant="h6"
            component="div"
            sx={{
              color: 'success.main',
              fontFamily: collectionTheme?.questionnaireTextFontFamily
            }}
          >
            Your answer was uploaded successfully!
          </Typography>
        </Grid>
      ) : (
        <Grid
          item
          xs={12}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 10, flexFlow: 'column' }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 100, color: 'error.main' }} />
          <Typography
            mt={2}
            variant="h6"
            component="div"
            sx={{
              color: 'error.main',
              fontFamily: collectionTheme?.questionnaireTextFontFamily
            }}
          >
            Upload failed
          </Typography>
          <Typography
            mt={1}
            variant="body1"
            component="div"
            textAlign="center"
            sx={{
              maxWidth: 560,
              color: collectionTheme?.questionnaireBodyTextColor,
              fontFamily: collectionTheme?.questionnaireTextFontFamily
            }}
          >
            {`We tried ${maxAttempts} times but your answer could not be uploaded. Your recording is saved on this device - please check your internet connection and try again.`}
          </Typography>
          {uploadError && (
            <Typography
              mt={1}
              variant="caption"
              component="div"
              textAlign="center"
              sx={{
                color: collectionTheme?.questionnaireBodyTextColor,
                fontFamily: collectionTheme?.questionnaireTextFontFamily
              }}
            >
              {`Error details: ${uploadError}`}
            </Typography>
          )}
          <Button
            variant="contained"
            sx={{
              mt: 3,
              color: collectionTheme?.questionnaireContainedButtonTextColor,
              backgroundColor: collectionTheme?.questionnaireContainedButtonBackgroundColor,
              ':hover': { backgroundColor: collectionTheme?.questionnaireContainedButtonHoverColor },
              fontFamily: collectionTheme?.questionnaireTextFontFamily
            }}
            onClick={() => file && handleNext(file)}
            disabled={!file}
          >
            Try again
          </Button>
        </Grid>
      )}
      <Grid item xs={12} textAlign="center">
        <Box
          mb={3}
          mt={3}
          textAlign="center"
          sx={{
            color: collectionTheme?.questionnaireBodyTextColor,
            fontFamily: collectionTheme?.questionnaireTextFontFamily
          }}
        >
          <Typography
            textAlign={'center'}
            variant={'h6'}
            marginY={2}
            fontSize={'1em'}
            fontFamily={collectionTheme?.questionnaireTextFontFamily}
          >
            {'Recommendations for a helpful response: ' + questionObj.recommendation}
          </Typography>
          {invite.requestedMediaLength && (
            <Grid item xs={12} textAlign="center">
              <Typography
                variant="caption"
                mb={2}
                textAlign="center"
                fontSize="0.9rem"
                fontFamily={collectionTheme?.questionnaireTextFontFamily}
              >
                {`If possible, please keep your recording duration under ${String(
                  (duration.hours || 0) * 60 + (duration.minutes || 0)
                )} minute${(duration.minutes || 0) > 1 ? 's' : ''}${
                  duration.seconds ? ` and ${String(duration.seconds).padStart(2, '0')} seconds` : ''
                }.`}
              </Typography>
            </Grid>
          )}
          <Typography
            variant="caption"
            mt={2}
            textAlign="center"
            fontSize="0.9rem"
            fontFamily={collectionTheme?.questionnaireTextFontFamily}
          >
            Your response will be used in accordance with the{' '}
            <Link href={terms} target="_blank" sx={{ color: collectionTheme?.questionnaireLinksColor }}>
              {' '}
              {entityText}
            </Link>
            ,
            <Link href={policy} target="_blank" sx={{ color: collectionTheme?.questionnaireLinksColor }}>
              {' '}
              Privacy Policy
            </Link>
            {urlBase === 'ioc.rakonto.io' && (
              <>
                and ,{' '}
                <Link href={releaseForm} target="_blank" sx={{ color: collectionTheme?.questionnaireLinksColor }}>
                  {' '}
                  Release Form
                </Link>{' '}
              </>
            )}
          </Typography>
        </Box>
      </Grid>
      <Box
        sx={{
          fontSize: '0.8rem',
          display: 'flex',
          width: '100%',
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 11
        }}
      >
        <Box sx={{ flex: 1 }} />
        <ButtonGroup>
          <LoadingButton
            disabled={uploadState !== 'idle' && uploadState !== 'error'}
            onClick={() => handleNext(null)}
            sx={{
              fontSize: '0.8rem',
              color: collectionTheme?.questionnaireContainedButtonTextColor,
              backgroundColor: collectionTheme?.questionnaireContainedButtonBackgroundColor,
              ':hover': { backgroundColor: collectionTheme?.questionnaireContainedButtonHoverColor },
              fontFamily: collectionTheme?.questionnaireTextFontFamily
            }}
            variant="contained"
            size="medium"
          >
            Skip
          </LoadingButton>
          <LoadingButton
            onClick={() => handleNext(file)}
            disabled={!file || uploadState === 'success'}
            loading={isUploading}
            type="submit"
            sx={{
              fontSize: '0.8rem',
              color: collectionTheme?.questionnaireContainedButtonTextColor,
              backgroundColor: collectionTheme?.questionnaireContainedButtonBackgroundColor,
              ':hover': { backgroundColor: collectionTheme?.questionnaireContainedButtonHoverColor },
              fontFamily: collectionTheme?.questionnaireTextFontFamily,
              ':disabled': {
                backgroundColor: collectionTheme?.questionnaireContainedDisabledButtonBackgroundColor,
                color: collectionTheme?.questionnaireContainedDisabledButtonColor
              }
            }}
            variant="contained"
            size="medium"
          >
            Submit
          </LoadingButton>
        </ButtonGroup>
      </Box>
    </Grid>
  )
}

export default Step2
